// components/NotificationCenter.tsx
import { createPortal } from 'react-dom'
import NotificationsIcon from '@mui/icons-material/Notifications'
import RefreshIcon from '@mui/icons-material/Refresh'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Box, IconButton, Sheet, Stack, Typography, Tooltip } from '@mui/joy'
import { useMediaQuery } from '@mui/material'
import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { keyframes } from '@emotion/react'
import CloseIcon from '@mui/icons-material/Close'
import { useNotification, INotification } from 'hooks/useNotification'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from 'hooks/auth'
import { FeatureType } from '@numeo/types-core'
import { Chip } from '@mui/material'
import { WebSocketStatus } from '@numeo/types-core'
import { setNotificationSoundEnabled, useNotificationSoundEnabled } from 'utils/notificationSoundUtils'
import React from 'react'

// Pulse animation for the border
const pulse = keyframes`
  0% { border-color: transparent; }
  50% { border-color: currentColor; }
  100% { border-color: transparent; }
`

// Types for notification status and special notification topics
type NotificationStatus = 'opening' | 'showing' | 'closing' | 'none'
type SpecialTopic = 'temp' | 'duplicate_connection' | 'permanent'

// Constants for notification appearance
const NOTIFICATION_COLORS = {
    info: {
        background: 'success.softBg',
        border: 'success.500',
        text: 'success.600',
    },
    warning: {
        background: 'warning.softBg',
        border: 'warning.500',
        text: 'warning.700',
    },
    error: {
        background: 'danger.softBg',
        border: 'danger.500',
        text: 'danger.600',
    },
}

// Constants for animation timing
const ANIMATION_DURATION = 300 // ms
const DISPLAY_TIMES = {
    temp: 3000, // ms
    duplicate_connection: 8000, // ms
    permanent: Infinity, // permanent notifications stay until explicitly removed
}

// The internal component that creates the portal
const NotificationPortal = ({ isVisible }: { isVisible: boolean }) => {
    const navigate = useNavigate()
    const { wsStatus, notifications, clearAll, clearNotification } = useNotification()
    // Same breakpoint MasterLayout uses to switch the sidebar from a persistent column to a
    // hidden slide-in drawer — on mobile, `SidebarNotificationButton` isn't reachable without
    // opening that drawer first, so the idle floating list below stays as the fallback there.
    const isMobile = useMediaQuery('(max-width: 899px)')

    // UI state
    const [isHovered, setIsHovered] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const timeoutRef = useRef<NodeJS.Timeout>()

    // Sound toggle state — shared with the sidebar bell's toggle
    const isSoundEnabled = useNotificationSoundEnabled()

    // Drag state
    const STORAGE_KEY = 'notification_center_position'
    const [position, setPosition] = useState<{ x: number; y: number }>(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY)
            if (saved) return JSON.parse(saved)
        } catch (e) {
            console.log(e)
        }
        return { x: 8, y: 8 } // default top-right offset
    })
    const [isDragging, setIsDragging] = useState(false)
    const dragStartRef = useRef<{ mouseX: number; mouseY: number; posX: number; posY: number } | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const handleDragStart = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            setIsDragging(true)
            dragStartRef.current = {
                mouseX: e.clientX,
                mouseY: e.clientY,
                posX: position.x,
                posY: position.y,
            }
        },
        [position]
    )

    useEffect(() => {
        if (!isDragging) return

        const handleMouseMove = (e: MouseEvent) => {
            if (!dragStartRef.current) return
            const dx = e.clientX - dragStartRef.current.mouseX
            const dy = e.clientY - dragStartRef.current.mouseY

            // Calculate new position (right-based x, top-based y)
            const newX = Math.max(0, dragStartRef.current.posX - dx)
            const newY = Math.max(0, dragStartRef.current.posY + dy)

            // Clamp to viewport
            const maxX = window.innerWidth - 48
            const maxY = window.innerHeight - 48
            setPosition({ x: Math.min(newX, maxX), y: Math.min(newY, maxY) })
        }

        const handleMouseUp = () => {
            setIsDragging(false)
            dragStartRef.current = null
            // Save position to localStorage
            setPosition((pos) => {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(pos))
                return pos
            })
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isDragging])

    // Notification state
    const [newNotifications, setNewNotifications] = useState<INotification[]>([])
    const [tempNotification, setTempNotification] = useState<INotification | null>(null)
    const [tempNotificationStatus, setTempNotificationStatus] = useState<NotificationStatus>('none')
    const [specialNotificationTopic, setSpecialNotificationTopic] = useState<SpecialTopic>('temp')

    /**
     * Handle special notifications display and animation
     */
    const handleSpecialNotification = (specialNotif: INotification) => {
        // Start opening animation
        setTempNotificationStatus('opening')
        setTempNotification(specialNotif)
        setSpecialNotificationTopic(specialNotif.topic as SpecialTopic)
        setIsExpanded(true)

        // After opening animation completes, show the notification
        setTimeout(() => {
            setTempNotificationStatus('showing')
        }, ANIMATION_DURATION)

        // Get display time based on notification type
        const displayTime = DISPLAY_TIMES[specialNotif.topic as SpecialTopic] || DISPLAY_TIMES.temp

        // For permanent notifications, don't set up auto-closing
        if (specialNotif.topic === 'permanent' || specialNotif.topic === 'duplicate_connection') {
            return // Exit early, don't set up closing timers
        }

        // Start closing animation after display time
        setTimeout(() => {
            setTempNotificationStatus('closing')

            // Remove notification after closing animation completes
            setTimeout(() => {
                setTempNotificationStatus('none')
                setTempNotification(null)
                setIsExpanded(false)
                clearNotification(specialNotif)
            }, ANIMATION_DURATION)
        }, displayTime)
    }

    // Process new notifications
    useEffect(() => {
        if (notifications.length === 0) return

        // Identify new notifications
        const previouslyHighlighted = new Set(newNotifications)
        const newlyAdded = notifications.filter((n) => !previouslyHighlighted.has(n))

        if (newlyAdded.length === 0) return

        setNewNotifications(newlyAdded)

        // Find special notifications with priority order
        const duplicateConnectionNotif = newlyAdded.find((n) => n.topic === 'duplicate_connection')
        const permanentNotif = newlyAdded.find((n) => n.topic === 'permanent')
        const tempNotif = newlyAdded.find((n) => n.topic === 'temp')
        const specialNotif = duplicateConnectionNotif || permanentNotif || tempNotif

        // Handle special notification if found
        if (specialNotif) {
            handleSpecialNotification(specialNotif)
        }

        // eslint-disable-next-line
    }, [notifications])

    // Clear highlight from new notifications after a delay
    useEffect(() => {
        if (!isExpanded && !isHovered) return

        const timer = setTimeout(() => {
            setNewNotifications([]) // Clear highlighted notifications
        }, 1000)

        return () => clearTimeout(timer)
    }, [isExpanded, isHovered])

    // Effect to handle removal of permanent notifications
    useEffect(() => {
        // If we have a permanent notification displayed but it's no longer in the notifications list
        if (tempNotification && tempNotification.topic === 'permanent') {
            const stillExists = notifications.some((n) => n.id === tempNotification.id)
            if (!stillExists) {
                // The permanent notification was removed, close it
                setTempNotificationStatus('closing')

                // Remove notification after closing animation completes
                setTimeout(() => {
                    setTempNotificationStatus('none')
                    setTempNotification(null)
                    setIsExpanded(false)
                }, ANIMATION_DURATION)
            }
        }
    }, [notifications, tempNotification])

    // Event handlers
    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        setIsHovered(true)
    }

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsHovered(false)
            setIsExpanded(false)
        }, 200) // Small delay to prevent accidental closing
    }

    const clearNotifications = (e: React.MouseEvent) => {
        e.stopPropagation()
        clearAll()
        setNewNotifications([])
    }

    const toggleSound = (e: React.MouseEvent) => {
        e.stopPropagation()
        setNotificationSoundEnabled(!isSoundEnabled)
    }

    // Determine dot color and animation based on notification state
    const { dotColor, animation } = useMemo(() => {
        if (tempNotification) {
            // Set color based on notification type
            let color
            switch (tempNotification.type) {
                case 'info':
                    color = 'success.500'
                    break
                case 'warning':
                    color = 'warning.500'
                    break
                case 'error':
                    color = 'danger.500'
                    break
                default:
                    color = 'success.500'
            }
            return { dotColor: color, animation: `${pulse} 2s infinite` }
        }

        // Set color based on WebSocket status
        switch (wsStatus) {
            case WebSocketStatus.CONNECTED:
                return { dotColor: 'success.500', animation: 'unset' }
            case WebSocketStatus.CONNECTING:
                return { dotColor: 'warning.400', animation: `${pulse} 2s infinite` }
            case WebSocketStatus.DISCONNECTED:
                return { dotColor: 'warning.500', animation: `${pulse} 2s infinite` }
            default:
                return { dotColor: 'danger.500', animation: `${pulse} 2s infinite` }
        }
    }, [tempNotification, wsStatus])

    /**
     * Calculate notification width based on message length
     */
    const calculateNotificationWidth = (message: string): string => {
        // Base width for notification icon and padding
        const baseWidth = 80
        // Approximate width per character (in pixels)
        const charWidth = 8
        // Calculate width based on message length
        const contentWidth = Math.min(message?.length * charWidth, 500)
        // Add base width to content width
        const totalWidth = baseWidth + contentWidth

        // Return calculated width with a minimum of 250px and maximum of 500px
        return `${Math.max(250, Math.min(totalWidth, 500))}px`
    }

    /**
     * Format timestamp as relative time
     */
    const formatTimeAgo = (timestamp: number): string => {
        const now = new Date()
        const date = new Date(timestamp)
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

        if (seconds < 60) return 'just now'

        const minutes = Math.floor(seconds / 60)
        if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`

        const hours = Math.floor(minutes / 60)
        if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`

        const days = Math.floor(hours / 24)
        if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`

        return date.toLocaleDateString()
    }

    // Filter out special notifications and sort by timestamp (newest first)
    const displayedNotifications = useMemo(() => {
        return [...notifications]
            .filter((n) => !['temp', 'duplicate_connection', 'permanent'].includes(n.topic ?? ''))
            .sort((a, b) => b.timestamp - a.timestamp)
    }, [notifications])

    const notificationContent = (
        <Box
            ref={containerRef}
            sx={{
                position: 'fixed',
                top: position.y,
                right: position.x,
                zIndex: 1400,
                userSelect: isDragging ? 'none' : 'auto',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bottom: -8,
                    left: -8,
                    zIndex: -1,
                },
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Sheet
                id="notification-slug"
                variant="outlined"
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 'xl',
                    border: '2px solid',
                    borderColor: dotColor,
                    cursor: 'pointer',
                    boxShadow: 'md',
                    background: 'var(--joy-palette-background-surface)',

                    '&:hover': {
                        boxShadow: 'lg',
                        transform: 'translateY(-1px)',
                    },
                    width: tempNotification
                        ? tempNotificationStatus === 'closing' || tempNotificationStatus === 'opening'
                            ? '40px'
                            : specialNotificationTopic === 'duplicate_connection'
                              ? '650px'
                              : calculateNotificationWidth(tempNotification.message)
                        : isHovered
                          ? 'max-content'
                          : 'fit-content',
                    padding: isHovered ? '6px 100px 6px 12px' : '6px',
                    animation,
                    animationTimingFunction: 'ease-in-out',
                    color: dotColor,
                    transform: tempNotification ? 'scale(0.98)' : 'scale(1)',
                    opacity: 1,
                    backdropFilter: 'blur(8px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                {/* Drag handle */}
                <DragIndicatorIcon
                    onMouseDown={handleDragStart}
                    sx={{
                        fontSize: 16,
                        cursor: 'grab',
                        color: 'var(--joy-palette-text-tertiary)',
                        opacity: isHovered || isDragging ? 1 : 0,
                        width: isHovered || isDragging ? 'auto' : 0,
                        mr: isHovered || isDragging ? 0.25 : 0,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { color: 'var(--joy-palette-text-primary)' },
                        '&:active': { cursor: 'grabbing' },
                    }}
                />
                <NotificationsIcon
                    sx={{
                        fontSize: 20,
                        mr: tempNotification ? (tempNotificationStatus === 'closing' || tempNotificationStatus === 'opening' ? 0 : 1) : isHovered ? 1 : 0,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        color: 'var(--joy-palette-text-primary)',
                    }}
                />

                {/* Show temp notification message in icon */}
                {tempNotification ? (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                            opacity: tempNotificationStatus === 'closing' || tempNotificationStatus === 'opening' ? 0 : 1,
                            width: tempNotificationStatus === 'closing' || tempNotificationStatus === 'opening' ? 0 : '100%',
                            overflow: 'hidden',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform:
                                tempNotification && (tempNotificationStatus === 'closing' || tempNotificationStatus === 'opening')
                                    ? 'scale(0.95) translateX(-10px)'
                                    : 'scale(1) translateX(0)',
                        }}
                    >
                        <Typography
                            level="body-sm"
                            sx={{
                                fontWeight: 'bold',
                                color: NOTIFICATION_COLORS[tempNotification.type]?.text || 'text.primary',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                flexGrow: 1,
                            }}
                        >
                            {tempNotification.message}
                        </Typography>

                        {/* Add refresh button for duplicate connection notifications */}
                        {tempNotification.topic === 'duplicate_connection' && (
                            <Tooltip title="Refresh page" placement="top">
                                <IconButton
                                    size="sm"
                                    variant="soft"
                                    color="primary"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        window.location.reload()
                                    }}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                ) : (
                    <>
                        {/* Show new notification count badge */}
                        {newNotifications.filter((n) => !['temp', 'duplicate_connection'].includes(n.topic ?? '')).length > 0 && !isHovered && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '-6px',
                                    right: '-6px',
                                    backgroundColor: 'danger.500',
                                    borderRadius: '50%',
                                    color: 'var(--joy-palette-text-primary)',
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    border: '1px solid var(--joy-palette-background-surface)',
                                    minWidth: '18px',
                                    height: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {newNotifications.filter((n) => !['temp', 'duplicate_connection'].includes(n.topic ?? '')).length}
                            </Box>
                        )}

                        {/* Sound toggle icon in main notification button */}
                        {!isHovered && (
                            <Box
                                onClick={toggleSound}
                                sx={{
                                    position: 'absolute',
                                    top: '-4px',
                                    left: '-4px',
                                    backgroundColor: isSoundEnabled ? 'var(--joy-palette-success-400)' : 'var(--joy-palette-neutral-500)',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid var(--joy-palette-background-surface)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                    },
                                }}
                            >
                                {isSoundEnabled ? (
                                    <VolumeUpIcon sx={{ fontSize: '12px', color: 'white' }} />
                                ) : (
                                    <VolumeOffIcon sx={{ fontSize: '12px', color: 'white' }} />
                                )}
                            </Box>
                        )}

                        {/* Regular notification count text */}
                        <Typography
                            level="body-sm"
                            sx={{
                                opacity: isHovered ? 1 : 0,
                                width: isHovered ? 'auto' : 0,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                transform: isHovered ? 'scale(1)' : 'scale(0.95)',
                            }}
                        >
                            {displayedNotifications.length > 0 ? displayedNotifications.length : 'No'} new notifications
                        </Typography>
                    </>
                )}

                {/* Close button */}
                {isHovered && notifications.length > 0 && (
                    <Tooltip title="Clear all" placement="bottom">
                        <IconButton
                            size="sm"
                            variant="plain"
                            sx={{
                                borderRadius: 'xl',
                                border: '2px solid',
                                borderColor: 'neutral.100',
                                position: 'absolute',
                                right: 4,
                                '&:hover': { bgcolor: 'background.level2' },
                            }}
                            onClick={clearNotifications}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Sheet>

            {/* Dropdown menu */}
            {!tempNotification && (
                <Sheet
                    id="notification-dropdown"
                    variant="outlined"
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: '100%',
                        mt: 1,
                        width: 380,
                        borderRadius: 'xl',
                        boxShadow: 'lg',
                        backdropFilter: 'blur(10px)',
                        background: 'var(--joy-palette-background-surface)',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        transform: isHovered || isExpanded ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-8px)',
                        opacity: isHovered || isExpanded ? 1 : 0,
                        pointerEvents: isHovered || isExpanded ? 'auto' : 'none',
                        transformOrigin: 'top',
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                        }}
                    >
                        <Typography level="title-md" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <NotificationsIcon sx={{ fontSize: 20, color: 'var(--joy-palette-text-primary)' }} /> Notifications
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Drag handle in dropdown */}
                            <Tooltip title="Drag to move" placement="top">
                                <Box
                                    onMouseDown={handleDragStart}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 32,
                                        height: 32,
                                        borderRadius: 'md',
                                        cursor: 'grab',
                                        color: 'text.tertiary',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { bgcolor: 'background.level2', color: 'text.primary' },
                                        '&:active': { cursor: 'grabbing' },
                                    }}
                                >
                                    <DragIndicatorIcon sx={{ fontSize: 18 }} />
                                </Box>
                            </Tooltip>
                            {/* Sound Toggle Button */}
                            <Tooltip title={isSoundEnabled ? 'Sound off' : 'Sound on'} placement="top">
                                <IconButton
                                    size="sm"
                                    variant={isSoundEnabled ? 'soft' : 'outlined'}
                                    color={isSoundEnabled ? 'success' : 'neutral'}
                                    onClick={toggleSound}
                                    sx={{
                                        transition: 'all 0.2s ease',
                                        minWidth: '32px',
                                        minHeight: '32px',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                        },
                                    }}
                                >
                                    {isSoundEnabled ? (
                                        <VolumeUpIcon sx={{ fontSize: '18px', color: 'success.500' }} />
                                    ) : (
                                        <VolumeOffIcon sx={{ fontSize: '18px', color: 'neutral.600' }} />
                                    )}
                                </IconButton>
                            </Tooltip>

                            {/* Clear All Button */}
                            {displayedNotifications.length > 0 && (
                                <Tooltip title="Clear all notifications" placement="top">
                                    <IconButton size="sm" variant="soft" color="neutral" onClick={clearNotifications}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                    <Stack
                        spacing={1}
                        p={1.25}
                        sx={{
                            maxHeight: '440px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': { width: '6px' },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': { background: 'text.secondary', borderRadius: '10px' },
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'var(--joy-palette-text-secondary) transparent',
                        }}
                    >
                        {displayedNotifications.length > 0 ? (
                            displayedNotifications.map((notification, index) => (
                                <Sheet
                                    key={`${notification.timestamp}-${index}`}
                                    variant="soft"
                                    onClick={
                                        notification.onClick ||
                                        (() => {
                                            clearNotification(notification)
                                            if (notification.path) navigate(notification.path)
                                        })
                                    }
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 'xl',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'background.level2',
                                            transform: 'translateY(-2px)',
                                            boxShadow: 'md',
                                        },
                                        transition: 'all 0.3s ease',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        bgcolor: 'var(--joy-palette-background-surface)',
                                        borderLeft: '4px solid',
                                        borderLeftColor: NOTIFICATION_COLORS[notification.type]?.border || 'primary.500',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    }}
                                >
                                    {/* Category indicator */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            width: '20px',
                                            height: '20px',
                                            bgcolor: NOTIFICATION_COLORS[notification.type]?.border || 'primary.500',
                                            clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                                            opacity: 0.7,
                                        }}
                                    />

                                    <Stack>
                                        <Stack direction="row" alignItems="center" width={'100%'} justifyContent={'space-between'}>
                                            <Typography
                                                level="title-md"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: 'var(--joy-palette-text-primary)',
                                                }}
                                            >
                                                {notification.message}
                                            </Typography>
                                            {notification.topic && (
                                                <Chip
                                                    label={notification.topic}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: 'xl',
                                                        fontSize: '0.8rem',
                                                        height: '20px',
                                                        fontWeight: 'medium',
                                                        textTransform: 'capitalize',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                    }}
                                                    color="primary"
                                                />
                                            )}
                                            <Typography
                                                level="body-xs"
                                                sx={{
                                                    color: 'var(--joy-palette-text-tertiary)',
                                                    fontStyle: 'italic',
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                {formatTimeAgo(notification.timestamp)}
                                            </Typography>
                                        </Stack>

                                        {notification.secondaryMessage && (
                                            <Typography level="body-sm" sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                                {notification.secondaryMessage.split('\n').map((line, index) => (
                                                    <React.Fragment key={index}>
                                                        {line}
                                                        {notification.secondaryMessage && index !== notification.secondaryMessage.split('\n').length - 1 && (
                                                            <br />
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Sheet>
                            ))
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2, opacity: 0.7 }}>
                                <NotificationsIcon sx={{ fontSize: 24, mb: 0.5, color: 'var(--joy-palette-text-secondary)' }} />
                                <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                    No new notifications
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Sheet>
            )}
        </Box>
    )

    // Only render the portal if visible
    if (!isVisible) {
        return null
    }

    // The idle "browse notifications" bell + dropdown now lives in the sidebar
    // (`SidebarNotificationButton`), always visible instead of route-gated — on DESKTOP,
    // where the sidebar is a persistent column. On mobile the sidebar is a hidden
    // slide-in drawer (`MasterLayout`), so reading/clearing notifications would otherwise
    // require opening the hamburger nav first; keep the floating idle list there as the
    // reachable fallback. Either way, this floating portal keeps doing its other job — the
    // urgent "special" toasts (duplicate-tab warning, temp call-center pings) — so it
    // renders on mobile regardless, and on desktop only while one of those is active.
    if (!tempNotification && !isMobile) {
        return null
    }

    return createPortal(notificationContent, document.body)
}

// Wrapper component that returns a JSX element instead of a portal directly
export const NotificationComponent: React.FC = () => {
    const location = useLocation()
    const { application } = useAuth()

    // Check if user has updater features enabled
    const hasUpdaterFeature =
        application?.features?.some((f) => (f.feature === FeatureType.Updater || f.feature === FeatureType.UpdaterLite) && f.enabled) ?? false

    // Check if on updater-agent page (not hub - hub has its own compact status pill)
    const isOnUpdaterRoute = location.pathname.includes('/updater-agent')

    // Check if on settings page
    const isOnSettingsRoute = location.pathname.startsWith('/settings')

    // Check if on dispatcher hub pages that should show notifications. The agentic-hub
    // page (/dispatcher/ai-hub) has its own dense in-page surfaces (chat pill, autonomous
    // run cards, AH_Error toasts) that overlap with this center's portal — explicitly
    // exclude it so the two don't visually fight for the bottom-right corner.
    const isOnDispatcherHubRoute = location.pathname.includes('/dispatcher/load-hub')

    // Box Truck loadboard shows the center too — driver-bid pings land here (BoxTruckBidNotifications
    // upserts each bid) so an auto-dismissed bid card stays reviewable.
    const isOnBoxTruckLoadboard = location.pathname.includes('/dispatcher/box-truck-loadboard')

    // Show notifications on updater-agent pages, settings page (if user has updater features), or dispatcher hub pages
    const shouldShowNotifications = isOnUpdaterRoute || isOnDispatcherHubRoute || isOnBoxTruckLoadboard || (isOnSettingsRoute && hasUpdaterFeature)

    // IMPORTANT: Always render NotificationPortal to avoid React hooks order issues.
    // The isVisible prop controls whether the portal content is rendered.
    // This ensures hooks inside NotificationPortal are always called in the same order.
    return <NotificationPortal isVisible={shouldShowNotifications} />
}
